// Bottom sheet that lets a project participant invite someone specific
// (first name + phone + optional "Because…" note) into a project. Creates
// a pending invitation attributed to the current user. In the demo no
// SMS is sent — the invitation waits in the session's data and becomes
// real provenance the moment the invitee onboards with this phone and
// pledges. See Feb 27, 2026 "Invite Others" audit.
import { useEffect, useRef, useState } from "react";
import { X, Send } from "lucide-react";

export function InviteSheet({ open, onClose, onSend, projectTitle }) {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const nameRef = useRef(null);

  useEffect(() => {
    if (open) {
      setName(""); setPhone(""); setNote(""); setError(""); setBusy(false);
      setTimeout(() => nameRef.current?.focus(), 60);
    }
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  if (!open) return null;

  const canSend = name.trim().length >= 1 && phone.trim().length >= 3 && !busy;

  const submit = async (e) => {
    e.preventDefault();
    if (!canSend) return;
    setBusy(true);
    setError("");
    try {
      await onSend({ inviteeName: name.trim(), inviteePhone: phone.trim(), note: note.trim() || undefined });
    } catch (err) {
      setError(err?.response?.data?.detail || "Something went wrong. Try again.");
      setBusy(false);
    }
  };

  return (
    <div
      className="c-invite-backdrop"
      role="dialog"
      aria-modal="true"
      aria-labelledby="invite-sheet-title"
      onClick={onClose}
      data-testid="invite-sheet-backdrop"
    >
      <div className="c-invite-sheet" onClick={(e) => e.stopPropagation()}>
        <button
          type="button"
          className="c-invite-close"
          onClick={onClose}
          aria-label="Close invite sheet"
          data-testid="invite-sheet-close"
        >
          <X size={18} strokeWidth={2} />
        </button>

        <div id="invite-sheet-title" className="c-invsheet-title">
          Who would you like to bring into this?
        </div>
        <p className="c-invsheet-sub">
          {projectTitle
            ? <>An invitation from you to join <em>{projectTitle}</em>.</>
            : "An invitation from you."}
        </p>

        <form onSubmit={submit} className="c-invite-form">
          <label className="c-invite-label" htmlFor="invite-name">Their first name</label>
          <input
            id="invite-name"
            ref={nameRef}
            className="c-invite-input"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g. Priya"
            autoComplete="off"
            data-testid="invite-name-input"
            maxLength={40}
          />

          <label className="c-invite-label" htmlFor="invite-phone">Phone</label>
          <input
            id="invite-phone"
            className="c-invite-input"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="Their phone number"
            inputMode="tel"
            autoComplete="off"
            data-testid="invite-phone-input"
            maxLength={40}
          />

          <label className="c-invite-label" htmlFor="invite-note">
            Because… <span className="c-invite-optional">(optional)</span>
          </label>
          <textarea
            id="invite-note"
            className="c-invite-textarea"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="A short reason why you thought of them."
            rows={3}
            data-testid="invite-note-input"
            maxLength={280}
          />

          {error ? (
            <div className="c-invite-error" data-testid="invite-error">{error}</div>
          ) : null}

          <button
            type="submit"
            className="c-btn c-btn-primary c-invite-send"
            disabled={!canSend}
            data-testid="invite-send-btn"
          >
            <Send size={15} strokeWidth={2} />
            <span>{busy ? "Sending…" : "Send invitation"}</span>
          </button>

          <p className="c-invite-fineprint">
            In this demo, no SMS is actually sent. Your invitation is saved with your
            name attached — the moment they visit Commons with this phone, they'll see it
            from you.
          </p>
        </form>
      </div>
    </div>
  );
}
